<div class="monitoring-container">
  <!-- Header -->
  <div class="monitoring-header">
    <div class="header-title">
      <mat-icon>monitor_heart</mat-icon>
      <h2>Rendszer Monitoring</h2>
    </div>
    <div class="header-actions">
      <div class="interval-selector">
        <span class="interval-label">Frissítés:</span>
        <div class="interval-buttons">
          @for (opt of refreshIntervals; track opt.value) {
            <button
              class="interval-btn"
              [class.active]="selectedInterval.value === opt.value"
              (click)="setRefreshInterval(opt)">
              {{ opt.label }}
            </button>
          }
        </div>
      </div>
      @if (lastRefresh) {
        <span class="refresh-indicator">
          Utolsó refresh időpontja: {{ lastRefresh | date:'HH:mm:ss' }}
        </span>
      }
      <button class="btn btn-outline btn-sm" (click)="refreshMetrics()" [disabled]="isLoading">
        <mat-icon [class.spinning]="isLoading">refresh</mat-icon>
        Frissítés
      </button>
    </div>
  </div>

  <!-- Két oszlopos elrendezés: bal = monitoring, jobb = backend logok -->
  <div class="monitoring-grid">
    <div class="monitoring-left-col">

  <!-- Loading state -->
  @if (isLoading && !systemHealth) {
  <div class="loading-overlay">
    <mat-icon class="spinning">hourglass_empty</mat-icon>
    <span>Metrikák betöltése...</span>
  </div>
  }

  <!-- Error state -->
  @if (errorMessage && !systemHealth) {
  <div class="error-message">
    <mat-icon>error_outline</mat-icon>
    <span>{{ errorMessage }}</span>
    <button class="btn btn-outline btn-sm" (click)="refreshMetrics()">Újrapróbálás</button>
  </div>
  }

  <!-- Metrics Grid -->
  @if (systemHealth) {
  <div class="metrics-grid">
    
    <!-- CPU Card -->
    <div class="metric-card" [class]="getStatusClass(systemHealth.cpu_percent)">
      <div class="card-header">
        <div class="card-icon">
          <mat-icon>memory</mat-icon>
        </div>
        <div class="card-title">
          <h3>CPU használat</h3>
          <span class="card-subtitle">{{ systemHealth.cpu_count }} mag ({{ systemHealth.cpu_count_logical }} logikai)</span>
        </div>
        <mat-icon class="status-icon" [matTooltip]="systemHealth.cpu_percent < 60 ? 'Normál' : (systemHealth.cpu_percent < 80 ? 'Figyelem' : 'Kritikus')">
          {{ getStatusIcon(systemHealth.cpu_percent) }}
        </mat-icon>
      </div>
      <div class="card-body">
        <div class="metric-value">
          <span class="value">{{ systemHealth.cpu_percent | number:'1.1-1' }}%</span>
        </div>
        <mat-progress-bar 
          [mode]="'determinate'" 
          [value]="systemHealth.cpu_percent"
          [color]="getProgressBarColor(systemHealth.cpu_percent)">
        </mat-progress-bar>
        <div class="metric-details">
          <div class="detail-row">
            <span class="label">Load Average:</span>
            <span class="value">{{ systemHealth.load_avg_1min | number:'1.2-2' }} / {{ systemHealth.load_avg_5min | number:'1.2-2' }} / {{ systemHealth.load_avg_15min | number:'1.2-2' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Memory Card -->
    <div class="metric-card" [class]="getStatusClass(systemHealth.memory_percent)">
      <div class="card-header">
        <div class="card-icon">
          <mat-icon>sd_card</mat-icon>
        </div>
        <div class="card-title">
          <h3>RAM használat</h3>
          <span class="card-subtitle">{{ formatMbToGb(systemHealth.memory_total_mb) }} összes</span>
        </div>
        <mat-icon class="status-icon" [matTooltip]="systemHealth.memory_percent < 60 ? 'Normál' : (systemHealth.memory_percent < 80 ? 'Figyelem' : 'Kritikus')">
          {{ getStatusIcon(systemHealth.memory_percent) }}
        </mat-icon>
      </div>
      <div class="card-body">
        <div class="metric-value">
          <span class="value">{{ systemHealth.memory_percent | number:'1.1-1' }}%</span>
          <span class="secondary">{{ formatMbToGb(systemHealth.memory_used_mb) }} / {{ formatMbToGb(systemHealth.memory_total_mb) }}</span>
        </div>
        <mat-progress-bar 
          [mode]="'determinate'" 
          [value]="systemHealth.memory_percent"
          [color]="getProgressBarColor(systemHealth.memory_percent)">
        </mat-progress-bar>
        <div class="metric-details">
          <div class="detail-row">
            <span class="label">Szabad:</span>
            <span class="value">{{ formatMbToGb(systemHealth.memory_available_mb) }}</span>
          </div>
          @if (systemHealth.swap_total_mb > 0) {
          <div class="detail-row">
            <span class="label">Swap:</span>
            <span class="value">{{ formatMbToGb(systemHealth.swap_used_mb) }} / {{ formatMbToGb(systemHealth.swap_total_mb) }} ({{ systemHealth.swap_percent | number:'1.0-0' }}%)</span>
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Disk Card -->
    <div class="metric-card" [class]="getStatusClass(systemHealth.disk_percent)">
      <div class="card-header">
        <div class="card-icon">
          <mat-icon>storage</mat-icon>
        </div>
        <div class="card-title">
          <h3>Disk használat</h3>
          <span class="card-subtitle">{{ systemHealth.disk_total_gb | number:'1.1-1' }} GB összes</span>
        </div>
        <mat-icon class="status-icon" [matTooltip]="systemHealth.disk_percent < 60 ? 'Normál' : (systemHealth.disk_percent < 80 ? 'Figyelem' : 'Kritikus')">
          {{ getStatusIcon(systemHealth.disk_percent) }}
        </mat-icon>
      </div>
      <div class="card-body">
        <div class="metric-value">
          <span class="value">{{ systemHealth.disk_percent | number:'1.1-1' }}%</span>
          <span class="secondary">{{ systemHealth.disk_used_gb | number:'1.1-1' }} GB / {{ systemHealth.disk_total_gb | number:'1.1-1' }} GB</span>
        </div>
        <mat-progress-bar 
          [mode]="'determinate'" 
          [value]="systemHealth.disk_percent"
          [color]="getProgressBarColor(systemHealth.disk_percent)">
        </mat-progress-bar>
        <div class="metric-details">
          <div class="detail-row">
            <span class="label">Szabad:</span>
            <span class="value">{{ systemHealth.disk_free_gb | number:'1.1-1' }} GB</span>
          </div>
          <div class="detail-row">
            <span class="label">I/O:</span>
            <span class="value">↓ {{ formatNetworkTraffic(systemHealth.disk_read_mb) }} / ↑ {{ formatNetworkTraffic(systemHealth.disk_write_mb) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Database Card -->
    @if (databaseHealth) {
    <div class="metric-card" [class]="getStatusClass(databaseHealth.connection_percent)">
      <div class="card-header">
        <div class="card-icon">
          <mat-icon>dns</mat-icon>
        </div>
        <div class="card-title">
          <h3>PostgreSQL</h3>
          <span class="card-subtitle">Adatbázis állapot</span>
        </div>
        <mat-icon class="status-icon" [matTooltip]="databaseHealth.connection_percent < 60 ? 'Normál' : (databaseHealth.connection_percent < 80 ? 'Figyelem' : 'Kritikus')">
          {{ getStatusIcon(databaseHealth.connection_percent) }}
        </mat-icon>
      </div>
      <div class="card-body">
        <div class="metric-value">
          <span class="value">{{ databaseHealth.total_connections }} / {{ databaseHealth.max_connections }}</span>
          <span class="secondary">összes kapcsolat</span>
        </div>
        <mat-progress-bar 
          [mode]="'determinate'" 
          [value]="databaseHealth.connection_percent"
          [color]="getProgressBarColor(databaseHealth.connection_percent)">
        </mat-progress-bar>
        <div class="metric-details">
          <div class="detail-row">
            <span class="label">Aktív lekérdezések:</span>
            <span class="value">{{ databaseHealth.active_connections }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Kapcsolat %:</span>
            <span class="value">{{ databaseHealth.connection_percent | number:'1.1-1' }}%</span>
          </div>
          <div class="detail-row">
            <span class="label">DB méret:</span>
            <span class="value">{{ databaseHealth.database_size_mb | number:'1.2-2' }} MB</span>
          </div>
        </div>
      </div>
    </div>
    }

  </div>
  }

  <!-- Előzmény diagramok -->
  @if (systemHealth) {
  <div class="charts-section">
    <h3 class="charts-title">
      <mat-icon>show_chart</mat-icon>
      Előzmény diagramok
    </h3>

    <div class="charts-grid">
      <!-- CPU + Memória diagram -->
      <div class="chart-card">
        <div class="chart-header">
          <mat-icon>memory</mat-icon>
          <span>CPU &amp; Memória használat</span>
        </div>
        <div class="chart-body">
          <canvas baseChart
            [type]="'line'"
            [data]="cpuMemChartData"
            [options]="cpuMemChartOptions">
          </canvas>
        </div>
      </div>

      <!-- Adatbázis kapcsolatok diagram -->
      <div class="chart-card">
        <div class="chart-header">
          <mat-icon>dns</mat-icon>
          <span>Adatbázis kapcsolatok</span>
        </div>
        <div class="chart-body">
          <canvas baseChart
            [type]="'line'"
            [data]="dbChartData"
            [options]="dbChartOptions">
          </canvas>
        </div>
      </div>
    </div>
  </div>
  }

  <!-- Auto-refresh indicator -->
  @if (systemHealth) {
  <div class="refresh-indicator">
    <mat-icon>sync</mat-icon>
    <span>Automatikus frissítés: {{ selectedInterval.label }}-ként</span>
  </div>
  }
    </div>

    <div class="monitoring-right-col">
      <app-backend-logs-panel></app-backend-logs-panel>
    </div>
  </div>
</div>
